import { z } from 'zod';

// Tile size
export const TILE_SIZE = 32;
export const VIEWPORT_WIDTH = 720;
export const VIEWPORT_HEIGHT = 1280;

// Simulation
export const FIXED_TIMESTEP_MS = 16.666;
export const MAX_CATCHUP_STEPS = 5;
export const TARGET_FPS = 60;
export const MIN_FPS = 30;

// Player
export const PLAYER_SPEED = 230;
export const PLAYER_COLLIDER_RADIUS = 20;

// Lives
export const FREE_LIVES = 5;
export const FREE_LIVES_RESET_UTC = '00:00';
export const MAX_FREE_AMMO_UPGRADE = 5;

// Enemy
export const ENEMY_VISION_RADIUS = 280;
export const ENEMY_VISION_ANGLE = 100;
export const ENEMY_AI_TICK_HZ = 10;
export const ENEMY_ASTAR_MAX_PER_SECOND = 4;
export const ENEMY_SEARCH_DURATION_MS = 4000;

// Noise
export const NOISE_RADIUS_SHOT = 420;
export const NOISE_DURATION_MS = 4000;

// Combat
export const COMBAT_STUN_DURATION_MS = 250;
export const KNIFE_RANGE = 48;
export const KNIFE_COOLDOWN_MS = 550;

// Weapon types
export const WeaponTypeSchema = z.enum(['RIFLE', 'PISTOL', 'TOKEN_LAUNCHER']);
export type WeaponType = z.infer<typeof WeaponTypeSchema>;

export interface WeaponConfig {
  type: WeaponType;
  damage: number;
  fireRateMs: number;       // cooldown between shots
  projectileSpeed: number;  // px/s, 0 = hitscan
  noiseRadius: number;      // noise radius on shot
  magazineSize: number;     // max ammo (for display)
}

export const WEAPON_CONFIGS: Record<WeaponType, WeaponConfig> = {
  RIFLE: {
    type: 'RIFLE',
    damage: 1,
    fireRateMs: 400,
    projectileSpeed: 800,
    noiseRadius: NOISE_RADIUS_SHOT,
    magazineSize: 5,
  },
  PISTOL: {
    type: 'PISTOL',
    damage: 1,
    fireRateMs: 300,
    projectileSpeed: 900,
    noiseRadius: 360,
    magazineSize: 8,
  },
  TOKEN_LAUNCHER: {
    type: 'TOKEN_LAUNCHER',
    damage: 2,
    fireRateMs: 600,
    projectileSpeed: 600,
    noiseRadius: 500,
    magazineSize: 3,
  },
};

// Bundle
export const INITIAL_BUNDLE_SIZE_MB = 8;
export const MAX_MVP_BUNDLE_SIZE_MB = 20;

// Level configs
export const EnemyTypeSchema = z.enum([
  'BASIC_SCAMMER',
  'SUPPORT_SCAMMER',
  'ARMORED_SCAMMER',
  'SPAMMER',
  'INVESTOR_SCAMMER',
]);
export type EnemyType = z.infer<typeof EnemyTypeSchema>;

export const ENEMY_STATS: Record<EnemyType, { hp: number; speed: number }> = {
  BASIC_SCAMMER: { hp: 1, speed: 155 },
  SUPPORT_SCAMMER: { hp: 1, speed: 145 },
  ARMORED_SCAMMER: { hp: 2, speed: 130 },
  SPAMMER: { hp: 1, speed: 140 },
  INVESTOR_SCAMMER: { hp: 2, speed: 165 },
};

export const StageSchema = z.enum(['1', '2', '3']);
export type Stage = z.infer<typeof StageSchema>;

export const PointSchema = z.object({
  x: z.number(),
  y: z.number(),
});
export type Point = z.infer<typeof PointSchema>;

export const ObjectiveTypeSchema = z.enum(['collect_coins', 'activate_terminals', 'find_key', 'deliver_valuables', 'neutralize_targets', 'evacuate']);
export type ObjectiveType = z.infer<typeof ObjectiveTypeSchema>;

export const ObjectiveConfigSchema = z.object({
  type: ObjectiveTypeSchema,
  target: z.number().int(),
});
export type ObjectiveConfig = z.infer<typeof ObjectiveConfigSchema>;

export const EnemySpawnConfigSchema = z.object({
  type: EnemyTypeSchema,
  position: PointSchema,
  patrolPath: z.array(PointSchema).optional(),
});
export type EnemySpawnConfig = z.infer<typeof EnemySpawnConfigSchema>;

export const LootTypeSchema = z.enum(['COIN', 'VALUABLE', 'QUEST_ITEM', 'EVENT_ITEM', 'ENEMY_DROP']);
export type LootType = z.infer<typeof LootTypeSchema>;

export const LootSpawnConfigSchema = z.object({
  type: LootTypeSchema,
  position: PointSchema,
  value: z.number().int().optional(),
});
export type LootSpawnConfig = z.infer<typeof LootSpawnConfigSchema>;

export const LevelConfigSchema = z.object({
  id: z.string(),
  stage: StageSchema,
  mapId: z.string(),
  timeLimitSec: z.number().int().positive().nullable(),
  playerSpawn: PointSchema,
  objective: ObjectiveConfigSchema,
  secondaryObjective: ObjectiveConfigSchema.optional(),
  enemies: z.array(EnemySpawnConfigSchema),
  loot: z.array(LootSpawnConfigSchema),
  startingWeapon: z.string().nullable(),
  startingAmmo: z.number().int().min(0),
  unlocks: z.array(z.object({
    type: z.string(),
    id: z.string(),
  })),
  challenge: z.object({
    type: z.string(),
    description: z.string(),
    reward: z.object({
      type: z.string(),
      amount: z.number().int(),
    }),
  }).optional(),
  tutorialSteps: z.array(z.object({
    text: z.string(),
    position: PointSchema,
    trigger: z.string(),
  })).optional(),
});
export type LevelConfig = z.infer<typeof LevelConfigSchema>;

// Levels
export { levels, getLevelConfig, getAllLevelIds } from './levels/index';
